import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-dashboard-header',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    RouterModule,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  pageTitle = 'Dashboard';
  organisations: { id: string; name: string }[] = [];
  selectedOrganisationId: string = '';
  showSelectDropdown = false;
  isSSOLogin = false;
  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.pageTitle = this.getTitleFromUrl(this.router.url);
        this.showSelectDropdown = false;
      });
    // Set initial title
    this.pageTitle = this.getTitleFromUrl(this.router.url);
  }

  ngOnInit() {
    console.log('HeaderComponent initialized');

    // Check if this is an SSO login
    this.isSSOLogin = this.checkIfSSOLogin();
    console.log('Is SSO login:', this.isSSOLogin);

    // First fetch organizations, then set the selected ID from localStorage
    this.fetchOrganisations();

    // Add event listener for storage changes (in case another tab changes the selection)
    window.addEventListener('storage', (event) => {
      if (event.key === 'selectedOrganisationId') {
        this.selectedOrganisationId =
          event.newValue && event.newValue !== '' ? event.newValue : '';
        console.log(
          'Storage event: selectedOrganisationId updated to',
          event.newValue
        );
        this.cdr.detectChanges();
      }
    });
  }

  checkIfSSOLogin(): boolean {
    // SSO users have an allunited_access_token in localStorage
    return localStorage.getItem('allunited_access_token') !== null;
  }

  getSSOOrganisationName(): string {
    // For SSO users, get the organization name from localStorage
    // This is set during SSO login callback
    const storedName = localStorage.getItem('selectedOrganisationName');
    return storedName || 'Organization';
  }

  fetchOrganisations() {
    console.log('Fetching organizations...');
    // First get the stored ID from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    console.log('Stored ID from localStorage:', storedId);

    this.http
      .get<{ id: string; name: string }[]>(`${this.apiBaseUrl}/organizations`)
      .subscribe({
        next: (data) => {
          console.log('Organizations fetched:', data);
          this.organisations = data;

          // After we have the organizations, set the selected ID from localStorage
          if (storedId && storedId !== '') {
            this.selectedOrganisationId = storedId;
            console.log(
              'Set selectedOrganisationId to:',
              this.selectedOrganisationId
            );
          } else {
            this.selectedOrganisationId = '';
            console.log(
              'No storedId, set selectedOrganisationId to empty string'
            );
          }

          // Force change detection to update the select element
          setTimeout(() => {
            this.cdr.detectChanges();
            console.log(
              'After change detection, selectedOrganisationId:',
              this.selectedOrganisationId
            );
          }, 0);
        },
        error: (err) => {
          console.error('Error fetching organizations:', err);
          this.organisations = [];
          this.selectedOrganisationId = '';
        },
      });
  }

  onOrganisationChange(value: string) {
    console.log('Organization change event with value:', value);

    // Always keep selectedOrganisationId as a string
    this.selectedOrganisationId = value;

    // Store the selected organization ID in localStorage
    if (value && value !== '') {
      localStorage.setItem('selectedOrganisationId', value);
      console.log('Saved to localStorage:', value);
    } else {
      localStorage.removeItem('selectedOrganisationId');
      console.log('Removed from localStorage');
      // If we are on a route that requires an organisation, redirect to dashboard
      const orgRequiredRoutes = ['/resourcetypes', '/schedules'];
      if (
        orgRequiredRoutes.some((route) => this.router.url.startsWith(route))
      ) {
        this.router.navigate(['/dashboard']);
      }
    }

    // Force change detection
    this.cdr.detectChanges();

    // Refresh the current page
    window.location.reload();
  }

  getSelectedOrganisationName(): string | null {
    if (!this.selectedOrganisationId) {
      return null;
    }

    // Make sure we have organizations loaded
    if (!this.organisations || this.organisations.length === 0) {
      return null;
    }

    // Find the organization by ID - use strict comparison
    const selectedOrg = this.organisations.find(
      (org) => org.id === this.selectedOrganisationId
    );

    return selectedOrg ? selectedOrg.name : null;
  }

  getTitleFromUrl(url: string): string {
    if (url.startsWith('/dashboard')) {
      return 'Dashboard';
    }
    if (url.startsWith('/organisations')) {
      return 'Organisations';
    }
    if (url.startsWith('/resourcetypes')) {
      return 'Resource Types';
    }
    if (url.startsWith('/resources')) {
      return 'Resources';
    }
    if (url.startsWith('/schedules')) {
      return 'Schedules';
    }
    return 'AllUnited AI Planner';
  }

  logout(): void {
    this.authService.logout();
  }
}
